import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
    Box,
    FormControl,
    ListSubheader,
    Menu,
    MenuItem,
    Pagination,
    Select,
    Stack,
    Typography,
    styled,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    MantineReactTable,
    useMantineReactTable,
    type MRT_ColumnDef,
    type MRT_Row,
    type MRT_TableInstance,
} from 'mantine-react-table';
import moment from 'moment';
import Papa from 'papaparse';
import React, { CSSProperties, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
    selectTableReservations,
    setTableReservations,
} from '../../app/Slices/TableReservationsSlice';
import { CustomButton } from '../../layout/styledcomponents/CustomButton';
import { camelCaseToTitleCase, formatCurrencyShort } from '../../shared/utils';
import { MRT_TableOptions } from 'mantine-react-table';
import { theme as appTheme } from '../../shared/theme';

const StyledListHeader = styled(ListSubheader)({
    backgroundImage: 'var(--Paper-overlay)',
});

interface TableProps<T extends Record<string, any>> extends Partial<
    MRT_TableOptions<T>
> {
    columns: MRT_ColumnDef<T>[];
    data: T[];
    tableName: string;
    loading: boolean;
    columnVisibility?: Record<string, boolean>;
    onRowClick?: (row: T) => void;
    pagination?: { pageSize: number; pageIndex: number };
    linkType?: LinkType | undefined;
    linkState?: any;
    cellStyles?: any;
    maxHeight?: string;
    cursor?: string;
    sorting?: any;
    onSelectionChange?: (selectedRows: T[]) => void;
    projectedEarnings?: boolean;
    enableGlobalFilter?: boolean;
    /** Appended after the total in the footer, e.g. "users" → "Showing 1 to 10 of 48 users". */
    paginationItemLabel?: string;
    /** Hides the Mantine top toolbar; export menu opens via `GENERIC_TABLE_EXPORT_MENU_EVENT`. */
    hideTopToolbar?: boolean;
}

export const tableBodyCellsStyles: CSSProperties = {
    color: appTheme.palette.text.primary,
    padding: 10,
};

export const tableHeadCellStyles: CSSProperties = {
    fontWeight: '700',
};

/** Open export menu from an external trigger (e.g. Users page download icon). */
export const GENERIC_TABLE_EXPORT_MENU_EVENT = '7day-generic-table-export-open';

export enum LinkType {
    Reservation = 'reservation',
    Experience = 'experience',
    User = 'user',
    Bill = 'bill',
    Invoice = 'invoice',
    Client = 'client',
    Guest = 'guest',
}

const GenericTable = <T extends Record<string, any>>({
    columns,
    data,
    tableName,
    columnVisibility,
    loading,
    onRowClick,
    pagination,
    linkType,
    linkState,
    cellStyles,
    maxHeight,
    cursor,
    sorting,
    onSelectionChange,
    projectedEarnings,
    enableGlobalFilter = true,
    paginationItemLabel,
    hideTopToolbar = false,
    ...rest
}: TableProps<T>) => {
    const allReservations = useSelector(selectTableReservations);
    const dispatch = useDispatch();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        if (!hideTopToolbar) return;

        const onOpenExportMenu = (event: Event) => {
            const anchor = (event as CustomEvent<{ anchorEl: HTMLElement }>)
                .detail?.anchorEl;
            if (anchor) {
                setAnchorEl(anchor);
            }
        };

        window.addEventListener(
            GENERIC_TABLE_EXPORT_MENU_EVENT,
            onOpenExportMenu,
        );
        return () => {
            window.removeEventListener(
                GENERIC_TABLE_EXPORT_MENU_EVENT,
                onOpenExportMenu,
            );
        };
    }, [hideTopToolbar]);

    const handleExportRowsToCsv = (rows: MRT_Row<T>[]) => {
        const visibleColumns = table
            .getVisibleLeafColumns()
            .filter(
                (col) =>
                    ![
                        'mrt-row-select',
                        'actions',
                        'rowActions',
                        'mrt-row-actions',
                    ].includes(col.id),
            );

        const headers = visibleColumns.map(
            (col) => col.columnDef.header?.toString() || col.id,
        );

        const currencyKeys = [
            'bookingsTotal',
            'serviceFeeAmount',
            'expensesTotal',
            'amount',
        ];

        const data = rows.map((row) =>
            visibleColumns.map((col) => {
                const accessorKey =
                    (col.columnDef as any).accessorKey ?? col.id;

                const val =
                    typeof col.accessorFn === 'function'
                        ? col.accessorFn(row.original, row.index)
                        : row.original[accessorKey as keyof T];

                if (col.id === 'user') {
                    const firstName = row.original?.firstName ?? '';
                    const lastName = row.original?.lastName ?? '';
                    const email = row.original?.email ?? '';
                    return `${firstName} ${lastName} (${email})`;
                }
                if (accessorKey === 'lastLoginAt') {
                    if (!val) return '-';
                    return `${moment(val).format('DD/MM/YYYY')} ${moment(
                        val,
                    ).format('h:mm a')}`;
                }
                if (accessorKey === 'isActive') {
                    return val ? 'Enabled' : 'Disabled';
                }
                if (accessorKey === 'role') {
                    return val === 'guest_admin'
                        ? 'Company Admin'
                        : val === 'guest_department_admin'
                          ? 'Team Admin'
                          : 'Guest User';
                }
                if (currencyKeys.includes(accessorKey)) {
                    return formatCurrencyShort(Number(val));
                }
                if (val instanceof Date) {
                    return moment(val).format('DD/MM/YYYY');
                }
                if (
                    typeof val === 'string' &&
                    moment(val, moment.ISO_8601, true).isValid()
                ) {
                    return moment(val).format('DD/MM/YYYY');
                }
                if (accessorKey === 'onBehalf') {
                    return val === true
                        ? 'On Behalf'
                        : val === false
                          ? 'Blocked'
                          : '';
                }
                if (typeof val === 'boolean') {
                    return val ? 'Yes' : 'No';
                }
                if (accessorKey === 'services' && Array.isArray(val)) {
                    return val.map(camelCaseToTitleCase).join(', ');
                }
                return val?.toString?.() ?? '';
            }),
        );

        const csv = Papa.unparse({ fields: headers, data });

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${tableName}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleExportRowsToPdf = (rows: MRT_Row<T>[]) => {
        const doc = new jsPDF({ orientation: 'landscape' });

        const visibleColumns = table
            .getVisibleLeafColumns()
            .filter(
                (col) =>
                    ![
                        'mrt-row-select',
                        'actions',
                        'rowActions',
                        'mrt-row-actions',
                    ].includes(col.id),
            );

        const columnCount = visibleColumns.length;
        const fontSize =
            columnCount > 14
                ? 7
                : columnCount > 12
                  ? 8
                  : columnCount > 10
                    ? 9
                    : columnCount > 8
                      ? 10
                      : columnCount > 6
                        ? 11
                        : 12;

        const tableData = rows.map((row) =>
            visibleColumns.map((col) => {
                const accessorKey =
                    (col.columnDef as any).accessorKey ?? col.id;
                const val =
                    typeof col.accessorFn === 'function'
                        ? col.accessorFn(row.original, row.index)
                        : row.original[accessorKey as keyof T];

                if (col.id === 'user') {
                    const firstName = row.original?.firstName ?? '';
                    const lastName = row.original?.lastName ?? '';
                    const email = row.original?.email ?? '';
                    return `${firstName} ${lastName}\n${email}`;
                }

                if (accessorKey === 'lastLoginAt') {
                    if (!val) return '-';
                    return (
                        moment(val).format('DD/MM/YYYY') +
                        ' ' +
                        moment(val).format('h:mm a')
                    );
                }

                if (accessorKey === 'isActive') {
                    return val ? 'Enabled' : 'Disabled';
                }

                if (accessorKey === 'role') {
                    return val === 'guest_admin'
                        ? 'Company Admin'
                        : val === 'guest_department_admin'
                          ? 'Team Admin'
                          : 'Guest User';
                }

                if (
                    [
                        'bookingsTotal',
                        'serviceFeeAmount',
                        'expensesTotal',
                        'amount',
                    ].includes(accessorKey)
                ) {
                    return formatCurrencyShort(Number(val));
                }

                if (val instanceof Date) {
                    return moment(val).format('DD/MM/YYYY');
                }

                if (
                    typeof val === 'string' &&
                    moment(val, moment.ISO_8601, true).isValid()
                ) {
                    return moment(val).format('DD/MM/YYYY');
                }

                if (accessorKey === 'services' && Array.isArray(val)) {
                    return val.map(camelCaseToTitleCase).join(', ');
                }

                if (accessorKey === 'onBehalf') {
                    return val === true
                        ? 'On Behalf'
                        : val === false
                          ? 'Blocked'
                          : '';
                }

                if (accessorKey === 'recurringId') {
                    return val ? val : '-';
                }

                if (typeof val === 'boolean') {
                    return val ? 'Yes' : 'No';
                }

                return val?.toString?.() ?? '';
            }),
        );

        const tableHeaders = visibleColumns.map(
            (col) => col.columnDef.header?.toString() || col.id,
        );

        autoTable(doc, {
            head: [tableHeaders],
            body: tableData,
            headStyles: {
                fillColor: theme.palette.info.main,
                textColor: 0,
                fontStyle: 'bold',
                fontSize,
            },
            bodyStyles: { fontSize },
        });

        doc.save(`${tableName}.pdf`);
    };

    const columnsToBeRemoved = Object.entries(columnVisibility ?? {})
        .filter(([_, value]) => value === false)
        .map(([key]) => key);

    const filteredColumns = columns.filter((column) => {
        if (columnsToBeRemoved && column.accessorKey) {
            return !columnsToBeRemoved.includes(column.accessorKey);
        }
        if (columnsToBeRemoved && column.id) {
            return !columnsToBeRemoved.includes(column.id);
        }
        return true;
    });

    const renderExportMenuDropdown = (exportTable: MRT_TableInstance<T>) => (
        <Menu
            id='grouped-menu'
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            MenuListProps={{
                'aria-labelledby': 'basic-button',
                sx: { py: 0 },
            }}
        >
            <StyledListHeader color='primary' sx={{ fontWeight: '400' }}>
                Export All Rows
            </StyledListHeader>

            <MenuItem
                onClick={() =>
                    handleExportRowsToPdf(
                        exportTable.getPrePaginationRowModel().rows,
                    )
                }
                sx={{ color: theme.palette.textAccent.main }}
            >
                PDF
            </MenuItem>

            <MenuItem
                onClick={() =>
                    handleExportRowsToCsv(
                        exportTable.getPrePaginationRowModel().rows,
                    )
                }
                sx={{ color: theme.palette.textAccent.main }}
            >
                CSV
            </MenuItem>

            <StyledListHeader color='primary' sx={{ fontWeight: '400' }}>
                Export Page Rows
            </StyledListHeader>

            <MenuItem
                onClick={() =>
                    handleExportRowsToPdf(exportTable.getRowModel().rows)
                }
                sx={{ color: theme.palette.textAccent.main }}
            >
                PDF
            </MenuItem>

            <MenuItem
                onClick={() =>
                    handleExportRowsToCsv(exportTable.getRowModel().rows)
                }
                sx={{ color: theme.palette.textAccent.main }}
            >
                CSV
            </MenuItem>

            <StyledListHeader color='primary' sx={{ fontWeight: '400' }}>
                Export Selected Rows
            </StyledListHeader>

            <MenuItem
                onClick={() =>
                    handleExportRowsToPdf(
                        exportTable.getSelectedRowModel().rows,
                    )
                }
                disabled={exportTable.getSelectedRowModel().rows.length === 0}
                sx={{ color: theme.palette.textAccent.main }}
            >
                PDF
            </MenuItem>

            <MenuItem
                onClick={() =>
                    handleExportRowsToCsv(
                        exportTable.getSelectedRowModel().rows,
                    )
                }
                disabled={exportTable.getSelectedRowModel().rows.length === 0}
                sx={{ color: theme.palette.textAccent.main }}
            >
                CSV
            </MenuItem>
        </Menu>
    );

    const renderExportMenu = (exportTable: MRT_TableInstance<T>) => (
        <>
            <CustomButton
                disableRipple
                variant='outlined'
                color='primary'
                onClick={handleClick}
                size='small'
            >
                <FileDownloadIcon fontSize='small' />
            </CustomButton>
            {renderExportMenuDropdown(exportTable)}
        </>
    );

    const isInteractiveCellClick = (target: EventTarget | null) => {
        if (!target || !(target instanceof Element)) return false;
        return Boolean(
            target.closest(
                // Note: do NOT include `a[href]` here, otherwise row-level <Link> navigation is blocked.
                'input[type="checkbox"], [data-column-id="mrt-row-select"], .mantine-Checkbox-root, [data-column-id="rowActions"], button, [role="menu"]',
            ),
        );
    };

    const table = useMantineReactTable({
        columns: filteredColumns,
        data,
        enableRowSelection: true,

        paginationDisplayMode: 'pages',

        mantineBottomToolbarProps: {
            sx: {
                p: 0,
                m: 0,
                minHeight: 'auto',
                backgroundColor: theme.palette.background.paper,
            },
        },

        positionToolbarAlertBanner: 'none',
        enableFullScreenToggle: false,
        enableGlobalFilter,
        enableDensityToggle: false,
        ...rest,
        ...(hideTopToolbar ? { enableTopToolbar: false } : {}),

        mantinePaperProps: {
            withBorder: false,
            shadow: 'none',
            style: { width: '100%', border: 'none', boxShadow: 'none' },
        },

        mantineTableContainerProps: {
            sx: {
                maxHeight: maxHeight ?? 'none',
                border: 'none',
                boxShadow: 'none',
            },
        },

        initialState: {
            density: 'xs',
            pagination,
            ...(sorting && { sorting }),
        },

        state: { ...(rest.state ?? {}), isLoading: loading },

        mantineTableBodyRowProps: ({ row }) => {
            let rowLink: string = '';

            if (
                linkType === LinkType.Reservation &&
                ((row.original as any)?.moduleName ||
                    (row.original as any)?.module) &&
                ((row.original as any)?.bookingId || (row.original as any)?.id)
            ) {
                rowLink = `/reservations/${
                    row.original?.module?.name ||
                    row.original?.moduleName.replace(/ /g, '').toLowerCase()
                }/${row.original?.bookingId || row.original?.id}`;

                linkState = {
                    ...linkState,
                    params: row.original,
                    reservations: allReservations,
                    index: allReservations?.findIndex(
                        (value) => value === row.original?.id,
                    ),
                };
            } else if (
                linkType === LinkType.Experience &&
                (row.original as any)?.type &&
                (row.original as any)?.id
            ) {
                rowLink = `/viewExperiences/${row.original?.type
                    .replace(/ /g, '')
                    .toLowerCase()}/${row.original?.id}`;
            } else if (linkType === LinkType.User && row.original?.id) {
                rowLink = `/users/${(row.original?.firstName || 'user')
                    .replace(/ /g, '')
                    .toLowerCase()}/${row.original?.id}`;

                linkState = { ...linkState, params: row?.original };
            } else if (
                linkType === LinkType.Bill &&
                row.original?.ownerId &&
                row.original?.year
            ) {
                const projected = projectedEarnings ?? false;
                rowLink = `/host-payment/${row.original?.ownerId}/${row.original?.year}/${row.original?.month}?projected=${projected}`;
            } else if (linkType === LinkType.Invoice && row.original?.id) {
                rowLink = `/invoices/${row.original?.id}`;
                linkState = { ...linkState, params: row.original };
            } else if (
                linkType === LinkType.Client &&
                row.original?.name &&
                row.original?.id
            ) {
                rowLink = `/clients/${row.original?.name
                    .replace(/ /g, '')
                    .toLowerCase()}/${row.original?.id}`;
                linkState = { ...linkState, params: row.original };
            } else if (linkType === LinkType.Guest && row.original?.id) {
                rowLink = `/guests/${row.original?.id}/users`;
                linkState = { ...linkState, params: row.original };
            }

            // If `linkType` resolves to a route, use row-level <Link> navigation.
            // `onRowClick` can still run for side effects (analytics, opening modals, etc.).
            const useRowLink = Boolean(linkType && rowLink);

            return {
                ...(useRowLink ? { component: Link } : {}),
                state: linkState,
                to: useRowLink ? rowLink : undefined,
                sx: {
                    cursor:
                        cursor ??
                        (rowLink || onRowClick ? 'pointer' : undefined),
                    textDecoration: useRowLink ? 'none' : undefined,
                    borderBottom: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    padding: 2,
                    fontSize: '14px',
                },
                onClick: (e: React.MouseEvent) => {
                    if (isInteractiveCellClick(e.target)) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                    }
                    if (onRowClick) onRowClick(row.original);
                },
            };
        },

        mantineTableBodyCellProps: {
            style: { ...tableBodyCellsStyles, ...cellStyles },
        },

        mantineTableHeadCellProps: {
            style: { ...tableHeadCellStyles },
        },
        renderTopToolbarCustomActions: hideTopToolbar
            ? undefined
            : ({ table: toolbarTable }) => (
                  <Box style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {renderExportMenu(toolbarTable)}
                      <CustomButton
                          disableRipple
                          variant='contained'
                          color='secondary'
                          size='small'
                          onClick={() => {
                              toolbarTable.resetColumnFilters();
                              toolbarTable.setGlobalFilter('');
                          }}
                          sx={{ marginRight: '5px' }}
                          disabled={
                              !toolbarTable.getState().globalFilter &&
                              !toolbarTable
                                  .getState()
                                  .columnFilters.map(
                                      (columnFilter) => columnFilter.value,
                                  )
                                  .filter((item) => {
                                      if (item == null) return false;
                                      if (Array.isArray(item)) {
                                          return item.some(
                                              (sub) => sub != null,
                                          );
                                      }
                                      return true;
                                  }).length
                          }
                      >
                          Clear Filters
                      </CustomButton>
                  </Box>
              ),

        renderBottomToolbar: ({ table }) => {
            const totalCount = table.getPrePaginationRowModel().rows.length;
            const pageSize = table.getState().pagination.pageSize;
            const pageIndex = table.getState().pagination.pageIndex;
            const rawPageCount = table.getPageCount();
            const pageCount = Math.max(1, rawPageCount || 1);
            const selectedCount = table.getSelectedRowModel().rows.length;

            const start = totalCount === 0 ? 0 : pageIndex * pageSize + 1;
            const end =
                totalCount === 0
                    ? 0
                    : Math.min(start + pageSize - 1, totalCount);

            const leftCaption =
                selectedCount > 0
                    ? `${selectedCount} selected of ${totalCount}`
                    : `Showing ${start} to ${end} of ${totalCount}${
                          paginationItemLabel ? ` ${paginationItemLabel}` : ''
                      }`;

            const pageSizeChoices = [5, 10, 25, 50, 100];
            const pageSizeOptions = pageSizeChoices.includes(pageSize)
                ? pageSizeChoices
                : [...pageSizeChoices, pageSize].sort((a, b) => a - b);

            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: { xs: 1.5, sm: 2 },
                        flexWrap: 'wrap',
                        width: '100%',
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 1.5, sm: 1.25 },
                        borderTop: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        backgroundColor: theme.palette.background.paper,
                        position: isMobile ? 'sticky' : 'static',
                        bottom: isMobile ? 0 : 'auto',
                        zIndex: isMobile ? 2 : 'auto',
                    }}
                >
                    <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{
                            width: { xs: '100%', sm: 'auto' },
                            textAlign: { xs: 'center', sm: 'left' },
                            flexShrink: 0,
                        }}
                    >
                        {leftCaption}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            flex: { xs: '0 0 auto', sm: '1 1 auto' },
                            minWidth: 0,
                            order: { xs: 3, sm: 0 },
                        }}
                    >
                        <Pagination
                            count={pageCount}
                            page={pageIndex + 1}
                            onChange={(_, value) =>
                                table.setPageIndex(value - 1)
                            }
                            color='primary'
                            siblingCount={isMobile ? 0 : 1}
                            boundaryCount={isMobile ? 0 : 1}
                            showFirstButton
                            showLastButton
                            size={isMobile ? 'small' : 'medium'}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    borderRadius: 1,
                                    fontWeight: 500,
                                },
                                '& .MuiPaginationItem-root.Mui-selected': {
                                    fontWeight: 700,
                                },
                            }}
                        />
                    </Box>

                    <Stack
                        direction='row'
                        alignItems='center'
                        spacing={1}
                        sx={{
                            flexShrink: 0,
                            order: { xs: 2, sm: 0 },
                            width: { xs: '100%', sm: 'auto' },
                            justifyContent: {
                                xs: 'center',
                                sm: 'flex-end',
                            },
                        }}
                    >
                        <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{ display: { xs: 'none', md: 'block' } }}
                        >
                            Rows per page:
                        </Typography>
                        <FormControl size='small' sx={{ minWidth: 88 }}>
                            <Select
                                value={pageSize}
                                onChange={(e) =>
                                    table.setPageSize(Number(e.target.value))
                                }
                                variant='outlined'
                                aria-label='Rows per page'
                            >
                                {pageSizeOptions.map((n) => (
                                    <MenuItem key={n} value={n}>
                                        {n}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Box>
            );
        },
    });

    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(
                table.getSelectedRowModel().rows.map((row) => row.original),
            );
        }
    }, [table.getSelectedRowModel().rows, onSelectionChange]);

    useEffect(() => {
        const sortedIds = table
            .getSortedRowModel()
            .rows.map((row) => row.original?.bookingId || row.original?.id);
        dispatch(setTableReservations(sortedIds));
    }, [table.getSortedRowModel().rows, dispatch]);

    return (
        <>
            <MantineReactTable table={table} />
            {hideTopToolbar ? renderExportMenuDropdown(table) : null}
        </>
    );
};

export default GenericTable;
